/**
 * 认证相关接口（登录、登出等）
 * 对标 tv-linux 项目 src/apis/pbRq.js 和 src/views/phonePassword/phonePassword.js
 */

import { post, get, ApiResponse } from './api';
import { API_PATHS } from './apiPaths';
import { getCookieAmend, setGuid } from '@/utils/apiUtils';
import { getEncryptedFormDataLogin } from '@/utils/encrypt';

/**
 * 获取二维码信息（扫码登录）
 * 对标 tv-linux 项目 login.js 的 GetQrInfo 方法
 *
 * @returns 返回二维码信息
 * - errCode: 0 表示成功
 * - sUrl: 二维码链接地址
 * - session: 会话ID，用于后续轮询
 */
export async function getQrInfo(): Promise<ApiResponse<any>> {
  // 确保 guid 已生成
  setGuid();
  const guid = getCookieAmend('guid');

  // 根据域名判断 appId
  // wetv 使用 10002，iflix 使用 10003
  let appId = '10002';
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'tv.iflix.com' || hostname === 'test.iflix.com') {
      appId = '10003';
    }
  }

  // 对标 tv-linux 的入参（注意字段名必须完全一致）
  const body = {
    appId, // 后台分配，wetv使用10002，iflix使用10003
    scene: 1, // 场景，后台分配
    deviceId: guid, // guid
  };

  console.log('[getQrInfo] appId:', appId);
  console.log('[getQrInfo] guid:', guid);
  console.log('[getQrInfo] request body:', JSON.stringify(body, null, 2));

  // 完全对标 tv-linux 的调用方式
  return post(API_PATHS.GetQrInfo, body, {
    CSRF: true,
    originReturn: true,
    params: {},
  });
}

/**
 * 轮询获取授权状态（扫码登录）
 * 对标 tv-linux 项目 login.js 的 AuthPoll 方法
 */
export async function authPoll(body?: any, params?: any): Promise<ApiResponse<any>> {
  return post(API_PATHS.AuthPoll, body, { params, originReturn: true });
}

/**
 * 查询账号状态
 * 对标 tv-linux 项目 phoneLogin.js / emailLogin.js 的 checkAccount 方法
 *
 * 入参格式：
 * - 手机号: { account: { user_type: 3, area_id: areaCode, user_id: phoneNum } }
 * - 邮箱: { account: { user_type: 4, user_id: email } }
 *
 * 出参处理：
 * - res.ret === 0 且 res.data.err_code === 0 表示成功
 * - res.data.state: 1-账号格式非法, 2-跳注册, 3-跳重置密码, 4-跳登录
 * - res.ret === 15031 表示操作过于频繁
 */
export async function checkAccount(body?: any, params?: any): Promise<ApiResponse<any>> {
  return post(API_PATHS.checkAccount, body, { params, originReturn: true });
}

/**
 * 检查手机用户（手机号登录专用）
 * 对标 tv-linux 项目 phoneLogin.js 的 checkAccount 调用
 *
 * @param areaCode 区号，如 "86" 或 "+86"
 * @param phoneNum 手机号
 * @returns 返回账号状态
 * - res.ret === 0 且 res.data.err_code === 0 表示成功
 * - res.data.state: 1-账号格式非法, 2-跳注册, 3-跳重置密码, 4-跳登录
 * - res.ret === 15031 表示操作过于频繁
 */
export async function checkUserByPhone(areaCode: string, phoneNum: string): Promise<ApiResponse<any>> {
  // 对标 tv-linux 入参格式，区号需要带 + 号
  const formattedAreaCode = areaCode.startsWith('+') ? areaCode : `+${areaCode}`;
  const body = {
    account: {
      user_type: 3, // 手机号类型
      area_id: formattedAreaCode,
      user_id: phoneNum,
    },
  };
  return post(API_PATHS.checkAccount, body, { originReturn: true });
}

/**
 * 检查邮箱用户（邮箱登录专用）
 * 对标 tv-linux 项目 emailLogin.js 的 checkAccount 调用
 *
 * @param email 邮箱地址
 * @returns 返回账号状态
 * - res.ret === 0 且 res.data.err_code === 0 表示成功
 * - res.data.state: 1-账号格式非法, 2-跳注册, 3-跳重置密码, 4-跳登录
 * - res.ret === 15031 表示操作过于频繁
 */
export async function checkUserByEmail(email: string): Promise<ApiResponse<any>> {
  // 对标 tv-linux 入参格式
  const body = {
    account: {
      user_type: 4, // 邮箱类型
      user_id: email,
    },
  };
  return post(API_PATHS.checkAccount, body, { originReturn: true });
}

/**
 * 发送验证码（手机号）
 */
export async function sendVerificationCode(phoneNum: string, areaCode: string): Promise<ApiResponse<any>> {
  return get(
    API_PATHS.SendOtp,
    {
      scene: 'tv_regist',
      account_str: JSON.stringify({
        user_id: phoneNum,
        user_type: 3,
        area_id: `+${areaCode}`,
      }),
    },
    {
      params: {
        video_appid: '1200012',
        append: 'true',
      },
    }
  );
}

/**
 * 验证验证码（手机号）
 */
export async function verifyCode(phoneNum: string, areaCode: string, vcode: string): Promise<ApiResponse<any>> {
  return post(
    API_PATHS.VerifyOtp,
    {
      scene: 'tv_regist',
      account_str: JSON.stringify({
        user_id: phoneNum,
        user_type: 3,
        area_id: `+${areaCode}`,
      }),
      otp: vcode,
    },
    {
      params: {
        video_appid: '1200012',
        append: 'true',
      },
    }
  );
}

/**
 * 手机号登录（验证码登录）
 */
export async function phoneLogin(body?: any, params?: any): Promise<ApiResponse<any>> {
  const { account, password, loginType, areaCode, phoneNum, vcode, ...rest } = body || {};

  // 如果是验证码登录
  if (vcode && phoneNum && areaCode) {
    return post(
      API_PATHS.phoneLogin,
      {
        kt_login: 'ph',
        country_id: areaCode,
        phone_id: phoneNum,
        auth_code: vcode,
        req_source: '0',
        is_ph_pwd: '0',
        ...rest,
      },
      {
        params: {
          vappid: '50560306',
          vsecret: 'f6b675fbb1ec757c3e4486954a06c21299b9ddaedb230ad6',
          ...params,
        },
      }
    );
  }

  // 兼容旧的调用方式
  return post(API_PATHS.phoneLogin, body, { params });
}

/**
 * 密码登录（手机号/邮箱）
 * 对标 tv-linux 项目 src/views/phonePassword/phonePassword.js 的 passwordClick 方法
 *
 * 入参格式（对标 tv-linux getEncryptedFormDataLogin）：
 * - account: 账号（手机号或邮箱）
 * - accountType: 账号类型（3=手机, 4=邮箱）
 * - areaNumber: 区号（手机登录时，如 "86" 或 "+86"）
 * - pwd: 密码
 *
 * 出参处理（对标 tv-linux phonePassword.js）：
 * - res.user_info 存在表示登录成功
 * - res.code === 401: 账户/密码不匹配
 * - res.code === 404: 手机号或邮箱未注册
 * - 其他 code: 服务异常
 *
 * @returns 登录结果
 */
export async function passwordLogin(params: {
  account: string;
  accountType: number;
  areaNumber?: string;
  pwd: string;
}): Promise<ApiResponse<any>> {
  const { account, accountType, areaNumber, pwd } = params;

  // 使用加密函数生成请求体（对标 tv-linux getEncryptedFormDataLogin）
  const body = await getEncryptedFormDataLogin({
    account,
    accountType,
    areaNumber,
    pwd,
  });

  return post(API_PATHS.phoneLogin, body, {
    originReturn: true,
  });
}

/**
 * 退出登录
 */
export async function logout(body?: any, params?: any): Promise<ApiResponse<any>> {
  return post(API_PATHS.Logout, body, { params, CSRF: true, originReturn: true });
}

/**
 * 票据续期
 */
export async function renew(body?: any, params?: any): Promise<ApiResponse<any>> {
  return post(API_PATHS.ReNew, body, { params });
}
